import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';

import { useActions } from '../../../../../hooks/useActions';
import { useProcessing } from '../../../../../hooks/useProcessing';
import { IMemberListItem } from '../../../../../models/MemberListItem';
import { IPerson } from '../../../../../models/Person';
import { RoutePath } from '../../../../../models/enums/RoutePath';
import { updateModalState } from '../../../../../store/redux-slices/modals';
import { MEMBERS_LOADING_PROCESSING } from '../../../../../store/redux-slices/processes/constants';
import { ILoadSubscriptionsPayload } from '../../../../../store/saga-slices/subscriptions/airAmbulance/models';
import { EDIT_MEMBER_MODAL_NAME } from '../../../../../styledComponents/Modals/EditMemberModal/editMemberModal.const';
import { textContainsWords } from '../../../../../utils/textHelpers';
import { clearSubscriptionsList } from '../../../../../store/redux-slices/subscriptions/common';
import { loadSubscriptions } from '../../../../../store/saga-slices/subscriptions/common';
import { useNavigateSearch } from '../../../../../hooks/useNavigateSearch';
import { MemberOverviewPageParamsType } from '../../../../../models/types/RoutePath';
import { RequiredQueryParams } from './srMembers.const';
import { loadSRMembers } from '../../../../../store/saga-slices/membership/securityResponse';
import {
  clearSRMembers,
  updateSRMembersList,
} from '../../../../../store/redux-slices/membership/securityResponse';
import { pageListSRMembersSelector } from '../../../../../store/redux-slices/membership/securityResponse/selectors';
import { srMembershipPlansPricesSelector } from '../../../../../store/redux-slices/pricing/securityResponse/selectors';
import { srLoadStandardPricing } from '../../../../../store/saga-slices/pricing/securityResponse';
import { clearSRMembershipPlansPrices } from '../../../../../store/redux-slices/pricing/securityResponse';
import { ILoadSRMembersPayload } from '../../../../../store/saga-slices/membership/securityResponse/models';

export const useSRMembers = () => {
  const LoadMembers = useActions(loadSRMembers);
  const ClearMembers = useActions(clearSRMembers);
  const location = useLocation();
  const navigate = useNavigateSearch();
  const pageListMembers = useSelector(pageListSRMembersSelector);
  const srMembershipPricing = useSelector(srMembershipPlansPricesSelector);

  const membersLoading = useProcessing(MEMBERS_LOADING_PROCESSING);
  const LoadStandardPricing = useActions(srLoadStandardPricing);
  const LoadSubscriptions = useActions(loadSubscriptions);
  const ClearSubscriptionsList = useActions(clearSubscriptionsList);
  const ClearStandardPricing = useActions(clearSRMembershipPlansPrices);

  const UpdateModalState = useActions(updateModalState);
  const UpdateMembersList = useActions(updateSRMembersList);

  const { id } = useParams();

  useEffect(() => {
    const payload: ILoadSubscriptionsPayload = {
      organizationId: id,
    };
    LoadSubscriptions(payload);
  }, [LoadSubscriptions, id]);

  useEffect(() => {
    LoadStandardPricing();
  }, [LoadStandardPricing]);

  useEffect(() => {
    const params = location.search.slice(1);

    if (!textContainsWords(params, RequiredQueryParams)) {
      return;
    }

    const payload: ILoadSRMembersPayload = {
      params,
      organizationId: id,
    };

    LoadMembers(payload);
  }, [LoadMembers, id, location.search]);

  useEffect(
    () => () => {
      ClearMembers();
      ClearStandardPricing();
      ClearSubscriptionsList();
    },
    [ClearMembers, ClearStandardPricing, ClearSubscriptionsList],
  );

  const onRowClickHandler = useCallback(
    (row: IMemberListItem) => {
      const params: MemberOverviewPageParamsType = {
        memberId: row.personId,
      };
      navigate(RoutePath.MemberOverviewPage, { params });
    },
    [navigate],
  );

  const rowEditHandler = useCallback(
    (personId: string) => {
      UpdateModalState([EDIT_MEMBER_MODAL_NAME, personId]);
    },
    [UpdateModalState],
  );

  const onMemberEditedHandler = useCallback(
    (person: IPerson) => {
      UpdateMembersList(person);
    },
    [UpdateMembersList],
  );

  return {
    pageListMembers,
    membersLoading,
    onRowClickHandler,
    rowEditHandler,
    onMemberEditedHandler,
    srMembershipPricing,
  };
};
